import logger from '../../util/logger';
import {
  FinancialAccountUserAssociateParams,
  MutationResponse,
} from './interfaces';
import { orgsRequest } from './request';

const FINANCIAL_ACCOUNT_NODE_USER_ASSOCIATE_MUTATION = `
mutation AssociateUserToFinancialAccountNode(
  $finAcctId: ID!
  $userId: ID!
  $isBeneficialOwner: Boolean
  $isContact: Boolean
  $isPrincipal: Boolean
  $isRepresentative: Boolean
  $kycId: ID
  $personId: ID
) {
  finAcctUserAssociationCreateResponse: finAcctUserAssociationCreate(
    finAcctId: $finAcctId
    userId: $userId
    isBeneficialOwner: $isBeneficialOwner
    isContact: $isContact
    isPrincipal: $isPrincipal
    isRepresentative: $isRepresentative
    kycId: $kycId
    personId: $personId
  ) {
    errors {
      message
      type
    }
    properties {
      acctId
      applicationId
      customerId
      finAcctId
      internal
      isBeneficialOwner
      isContact
      isPrincipal
      isRepresentative
      kybId
      kycId
      orgId
      processor
      status
      type
      userId
    }
    success
  }
}
`;

const FINANCIAL_ACCOUNT_NODE_USER_ASSOCIATION_UPDATE = `
mutation FinAcctUserAssociationUpdate(
  $finAcctId: ID!,
  $userId: ID!,
  $isBeneficialOwner: Boolean,
  $isRepresentative: Boolean,
  $kycId: ID,
  $personId: ID
  ) {
  finAcctUserAssociationUpdateResponse: finAcctUserAssociationUpdate(
    finAcctId: $finAcctId,
    userId: $userId,
    isBeneficialOwner: $isBeneficialOwner,
    isRepresentative: $isRepresentative,
    kycId: $kycId,
    personId: $personId
    ) {
    success
    errors {
      message
      type
    }
  }
}`;

const checkForErrors = (res: Record<string, Error[]>): void => {
  if (res.errors && res.errors.length > 0) {
    throw new Error(res.errors[0].message);
  }
};

const financialAccountUserAssociateVaultRequest = async (
  financialAccountUserAssociateParams: FinancialAccountUserAssociateParams,
): Promise<MutationResponse> => {
  const financialAccountAssociationRes = await orgsRequest(
    FINANCIAL_ACCOUNT_NODE_USER_ASSOCIATE_MUTATION,
    financialAccountUserAssociateParams,
  );
  const financialAccountAssociationJson =
    await financialAccountAssociationRes.json();
  const res =
    financialAccountAssociationJson.data.finAcctUserAssociationCreateResponse;
  checkForErrors(res);
  return res;
};

const financialAccountUserAssociationUpdate = async (params: {
  finAcctId: string;
  userId: string;
  isBeneficialOwner?: boolean;
  isRepresentative?: boolean;
  kycId?: string;
  personId?: string;
}): Promise<MutationResponse> => {
  const financialAccountUserAssociationUpdateRes = await orgsRequest(
    FINANCIAL_ACCOUNT_NODE_USER_ASSOCIATION_UPDATE,
    params,
  );
  const financialAccountUserAssociationUpdateJson =
    await financialAccountUserAssociationUpdateRes.json();
  const res =
    financialAccountUserAssociationUpdateJson.data
      .finAcctUserAssociationUpdateResponse;
  checkForErrors(res);
  return res;
};

export const financialAccountUserAssociate = async ({
  finAcctId,
  userId,
  isBeneficialOwner,
  kycId,
  personId,
  isRepresentative,
}: {
  finAcctId: string;
  userId: string;
  isBeneficialOwner?: boolean;
  kycId?: string;
  personId?: string;
  isRepresentative: boolean;
}): Promise<void> => {
  try {
    logger(
      `attempting to update association between user ${userId} and ${finAcctId}`,
    );
    await financialAccountUserAssociationUpdate({
      finAcctId,
      userId,
      isBeneficialOwner,
      isRepresentative,
      kycId,
      personId,
    });
  } catch (error) {
    logger(
      `error updating association between user ${userId} and ${finAcctId}. Attempting to create association instead`,
      false,
      error,
    );
    await financialAccountUserAssociateVaultRequest({
      finAcctId,
      userId,
      isBeneficialOwner,
      isRepresentative,
      kycId,
      personId,
    });
  }
};
